import React, { useEffect, useMemo, useState } from "react"

import type { Theme } from "@emotion/react"
import type { Interpolation } from "@emotion/serialize"
import { Checkbox } from "antd"
import type { CheckboxChangeEvent } from "antd/lib/checkbox"
import type {
    DeepMap,
    FieldError,
    FieldValues,
    UseFormRegister,
    UseFormSetValue,
    UseFormUnregister,
} from "react-hook-form"

import type { IIndexable } from "@common-shared/types/general.types.ts"

import { InputErrorElement } from "./inputError.element"

export const CheckboxElement: React.FunctionComponent<
    ICheckboxElementProps
> = props => {
    const elementFullName = useMemo<string>(() => {
        return `${props.fieldGroupName ? `${props.fieldGroupName}.` : ""}${
            props.name
        }`
    }, [props.name, props.fieldGroupName])

    const [isChecked, setIsChecked] = useState<boolean>(!!props.defaultChecked)

    useEffect(() => {
        if (props.reactHookFormRegister) {
            props.reactHookFormRegister(
                elementFullName,
                props.reactHookValidations || {},
            )

            if (
                typeof props.defaultChecked !== "undefined" &&
                props.reactHookFormSet
            ) {
                props.reactHookFormSet(elementFullName, props.defaultChecked)
            }
        }

        return () => {
            props.reactHookFormUnregister?.(elementFullName)
        }
    }, [props.reactHookFormRegister])

    // Watch for default value change and if it's requested update checkbox value
    useEffect(() => {
        if (
            !props.watchDefaultCheckedChange ||
            typeof props.defaultChecked === "undefined" ||
            props.defaultChecked === isChecked
        ) {
            return
        }

        setIsChecked(props.defaultChecked)
    }, [props.defaultChecked])

    // Watch for internal controlled isChecked value and update parents if needed
    useEffect(() => {
        props.onChange?.(isChecked)

        props.reactHookFormSet?.(elementFullName, isChecked)
    }, [isChecked])

    const onChange = (event: CheckboxChangeEvent) => {
        const value = event.target.checked

        setIsChecked(value)
    }

    const sizeStyles = useMemo<Interpolation<Theme> | undefined>(() => {
        switch (props.size) {
            case "big":
                return styles.bigSize

            case "default":
            default:
                return undefined
        }
    }, [props.size])

    return (
        <div
            className={`
                relative
                ${props.className}
            `}
        >
            <Checkbox
                className={props.checkboxClassName}
                disabled={props.disabled}
                checked={isChecked}
                onChange={onChange}
                css={sizeStyles}
            >
                {props.label}
            </Checkbox>

            {props.reactHookErrors?.[props.name] && (
                <InputErrorElement
                    text={
                        (props.reactHookErrors[props.name] as FieldError)
                            .message || "Error"
                    }
                />
            )}
        </div>
    )
}

const styles = {
    bigSize: {
        ".ant-checkbox-inner, .ant-checkbox-input": {
            transform: "scale(1.5)",
        },
    },
}

interface ICheckboxElementProps {
    className?: string
    checkboxClassName?: string
    name: string
    fieldGroupName?: string
    label?: string | React.JSX.Element
    reactHookFormRegister?: UseFormRegister<FieldValues>
    reactHookFormUnregister?: UseFormUnregister<FieldValues>
    reactHookValidations?: IIndexable
    reactHookFormSet?: UseFormSetValue<FieldValues>
    reactHookErrors?: DeepMap<FieldValues, FieldError>
    defaultChecked?: boolean
    watchDefaultCheckedChange?: boolean
    size?: "default" | "big"
    disabled?: boolean
    onChange?: (newValue: boolean) => void
}
